const CLASSIFICATION_LABELS: Record<string, string> = {
  INDIVIDUAL_FINALIDADE_DEFINIDA:   'Individual – Finalidade Definida',
  INDIVIDUAL_FINALIDADE_INDEFINIDA: 'Individual – Finalidade Indefinida',
  COLETIVA:                         'Coletiva',
  ESPECIAL:                         'Especial',
};

export const emendaClassificationLabel = (code?: string): string =>
  CLASSIFICATION_LABELS[(code || '').toUpperCase()] ?? code ?? '—';

export const emendaNumeroExercicio = (
  numeroEmenda?: number,
  exercicio?: number,
  officialCode?: string,
): { numero: string; exercicio: string } => {
  const codigo = officialCode?.match(/^[^-]+-([^-]+)-(\d{4})$/);
  return {
    numero: numeroEmenda != null ? String(numeroEmenda) : codigo?.[1] ?? 'Não informado',
    exercicio: exercicio != null ? String(exercicio) : codigo?.[2] ?? 'Não informado',
  };
};

export const emendaStatusTone = (status?: string): string => {
  const s = (status || '').trim().toLocaleLowerCase('pt-BR');
  const emerald = 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
  const red = 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300';
  const amber = 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  if (s === 'concluído' || s === 'concluido') return emerald;
  if (s === 'devolvido' || s.startsWith('devolvida')) return red;
  if (s.startsWith('retornada ao orçamento')) return amber;
  if (s === 'iniciado') return amber;
  if (s === 'em execução' || s === 'em execucao') return 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300';
  if (s === 'recebido') return 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-300';
  // fallback: detailed workflow labels (for backward compat / admin views)
  if (s.includes('aprovada') || s.includes('aprovado')) return emerald;
  if (s.includes('análise') || s.includes('analise')) return amber;
  return 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-700/50 dark:text-slate-300';
};

export const emendaHistoryTone = (acao?: string): { dot: string; badge: string } => {
  const s = (acao || '').toUpperCase();
  if (['APROVADA', 'APROVADO', 'ADMISSIBILIDADE_APROVADA', 'ANALISE_DEMANDA_APROVADA', 'ANALISE_DOCUMENTAL_APROVADA'].includes(s))
    return { dot: 'bg-emerald-500', badge: 'border-success-line bg-success-soft text-success-text' };
  if (['REPROVADA', 'REPROVADO', 'DEVOLVIDA_LEGISLATIVO', 'DEVOLVIDA_INCOMPATIBILIDADE_DEMANDA', 'DEVOLVIDA_INVIABILIDADE_DOCUMENTAL'].includes(s))
    return { dot: 'bg-red-500', badge: 'border-danger-line bg-danger-soft text-danger-text' };
  if (s === 'DEVOLVIDA' || s === 'RETORNO_ORCAMENTO_DEMANDA' || s === 'RETORNO_ORCAMENTO_DOCUMENTAL')
    return { dot: 'bg-amber-500', badge: 'border-warning-line bg-warning-soft text-warning-text' };
  if (s === 'EM_ANALISE_DOCUMENTAL') return { dot: 'bg-violet-500', badge: 'border-violet-200 bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:border-violet-800 dark:text-violet-300' };
  if (['EM_ANALISE_ADMISSIBILIDADE', 'EM_ANALISE_DEMANDA'].includes(s))
    return { dot: 'bg-sky-500', badge: 'border-info-line bg-info-soft text-info-fg' };
  if (s === 'RECEBIDO_CAMARA') return { dot: 'bg-blue-500', badge: 'border-info-line bg-info-soft text-info-fg' };
  return { dot: 'bg-slate-400', badge: 'border-line bg-surface-muted text-fg' };
};

const HISTORY_LABELS: Record<string, string> = {
  ANEXO_ADICIONADO: 'Anexo adicionado',
  CONTRATACOES_ATUALIZADAS: 'Contratações atualizadas',
  PROCESSO_NUMERADO: 'Processo numerado',
  PROCESSO_CORRIGIDO: 'Processo corrigido',
  VINCULO_ORCAMENTARIO_ATUALIZADO: 'Vínculo orçamentário atualizado',
  ANEXO_REMOVIDO: 'Anexo removido',
  SOLICITADA_APROVACAO: 'Solicitação de aprovação',
  APROVADA: 'Aprovada',
  APROVADO: 'Aprovada',
  ADMISSIBILIDADE_APROVADA: 'Admissibilidade aprovada',
  EM_ANALISE_ADMISSIBILIDADE: 'Em análise de admissibilidade',
  DEVOLVIDA_LEGISLATIVO: 'Devolvida ao Legislativo',
  EM_ANALISE_DEMANDA: 'Em análise de demanda',
  ANALISE_DEMANDA_APROVADA: 'Análise de demanda aprovada',
  RETORNO_ORCAMENTO_DEMANDA: 'Retornada ao Orçamento (demanda)',
  DEVOLVIDA_INCOMPATIBILIDADE_DEMANDA: 'Devolvida por incompatibilidade de demanda',
  EM_ANALISE_DOCUMENTAL: 'Em análise documental',
  ANALISE_DOCUMENTAL_APROVADA: 'Análise documental aprovada',
  RETORNO_ORCAMENTO_DOCUMENTAL: 'Retornada ao Orçamento (documental)',
  DEVOLVIDA_INVIABILIDADE_DOCUMENTAL: 'Devolvida por inviabilidade documental',
  DEVOLVIDA: 'Devolvida para retificação',
  REPROVADA: 'Reprovada',
  REPROVADO: 'Reprovada',
  CRIADA: 'Emenda criada',
  PENDENTE: 'Emenda criada',
  DETALHAMENTO_PENDENTE: 'Formulário de detalhamento pendente',
  DETALHAMENTO_ENVIADO: 'Formulário de detalhamento enviado',
  ATUALIZADA: 'Atualizada',
  MUDANCA_STATUS: 'Status alterado',
  'MUDANÇA_STATUS': 'Status alterado',
  RECEBIDO_CAMARA: 'Recebido da Câmara',
};

export const emendaHistoryLabel = (acao?: string): string =>
  HISTORY_LABELS[(acao || '').toUpperCase()] || acao || 'Atualização';

export const fMoney = (v?: number | string): string =>
  v != null && v !== ''
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v))
    : 'Não informado';

// Data sem horário ("2026-01-10") é lida por new Date() como meia-noite UTC e,
// no fuso de Brasília, aparece como o dia anterior. Formata direto da string.
export const fDate = (v?: string): string => {
  if (!v) return 'Não informado';
  const soData = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (soData) return `${soData[3]}/${soData[2]}/${soData[1]}`;
  return new Date(v).toLocaleDateString('pt-BR');
};

export const fDateTime = (v?: string): string =>
  v
    ? new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'Não informado';
